/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '@components/layout/page-header';
import Button from '@components/atoms/ui-kit/button.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import { Plus, Trash2 } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';
import toast from 'react-hot-toast';
import { debounce } from 'lodash';
import { InputActionMeta } from 'react-select';

import {
  Publication,
  useGetLanguageAndConfigPublication
} from '@utils/api/back-office/language-and-config/use-get-language-publications';
import { useCreatePublication } from '@utils/api/back-office/language-and-config/use-create-new-publication';
import { useDeletePublicationOrganization } from '@utils/api/back-office/language-and-config/use-delete-language-publication';
import { useGetCustomersToSellBo } from '@utils/api/back-office/customer-bo/use-get-customer-sell-bo';
import { CustomerBo } from '@utils/types/back-office/customers';
import { LabelValueSelectInput } from '@utils/types/shared/label-value-select-input';
import { GridAction } from '@components/atoms/datatable/grid-action-controller';

type Props = { languageId: number };
type FormValues = { organization_id: string };

export default function PublicationsPanel({ languageId }: Props) {
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPublication, setSelectedPublication] =
    useState<Publication | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');

  const { control, reset } = useForm<FormValues>({
    defaultValues: { organization_id: '' }
  });

  const {
    data: publications,
    isLoading,
    isError,
    isFetching
  } = useGetLanguageAndConfigPublication(languageId);
  const createPublication = useCreatePublication(languageId);
  const deletePublicationOrg = useDeletePublicationOrganization();

  const { data: customers } = useGetCustomersToSellBo(customerSearch);
  const customerList = useMemo(() => customers?.result ?? [], [customers]);

  const customerOptions: LabelValueSelectInput[] = useMemo(
    () =>
      customerList.map((c: CustomerBo) => ({
        label: `${c.name}  (#${c.id})`,
        value: String(c.id)
      })),
    [customerList]
  );

  const columns = useMemo<DataGridColumn<Publication>[]>(() => {
    return [
      {
        id: 'id',
        header: t('id'),
        field: 'id',
        maxWidth: 100,
        minWidth: 80,
        enableSorting: false,
        enableFiltering: false,
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'organization_id',
        header: t('organization-id'),
        field: 'organization_id',
        minWidth: 220,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'number',
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'organization_name',
        header: t('organization-name'),
        field: 'organization_name',
        minWidth: 320,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text',
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      }
    ];
  }, [t]);

  const rows = useMemo<Publication[]>(() => publications ?? [], [publications]);

  // Remember clicked row; do not open modals on row click by default
  const handleCellClick = (row: Publication, _field: string) => {
    if (!row?.id) return;
    setSelectedPublication(row);
  };

  /* ---------- Actions column (left-click) ---------- */
  const actions = useMemo<GridAction<Publication>[]>(
    () => [
      {
        id: 'delete',
        label: t('delete'),
        icon: <Trash2 className="h-3.5 w-3.5" />,
        intent: 'danger',
        run: row => {
          setSelectedPublication(row);
          setDeleteModalOpen(true);
        }
      }
    ],
    [t]
  );

  /* ---------- Right-click context menu ---------- */
  const menuOptions = useMemo(
    () => [
      { id: 'delete', label: t('delete'), icon: <Trash2 className="h-4 w-4" /> }
    ],
    [t]
  );

  const handleSetMenuOptions = (item: Publication) => {
    setSelectedPublication(item);
  };

  const handleMenuOptionClick = (optionId: string) => {
    if (!selectedPublication) return;
    if (optionId === 'delete') setDeleteModalOpen(true);
  };

  const onSubmitCreate = (values: FormValues) => {
    if (!values.organization_id) return;
    createPublication.mutate(
      { organization_id: values.organization_id },
      {
        onSuccess: () => {
          setIsModalOpen(false);
          reset({ organization_id: '' });
        }
      }
    );
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((v: string) => {
        const q = v.trim();
        setCustomerSearch(q.length >= 2 ? q : '');
      }, 300),
    []
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleInputChange = useCallback(
    (value: string, meta?: InputActionMeta) => {
      if (!meta || meta.action === 'input-change') {
        debouncedSearch(value);
      }
      return value;
    },
    [debouncedSearch]
  );

  const handleConfirmDelete = () => {
    if (!selectedPublication?.organization_id) return;
    const loadingToastId = toast.loading(t('deleting-publication'));

    deletePublicationOrg.mutate(
      {
        languageId,
        organizationId: selectedPublication.organization_id
      },
      {
        onSuccess: () => {
          toast.success(t('publication-deleted-successfully'), {
            id: loadingToastId
          });
          setDeleteModalOpen(false);
        },
        onError: () => {
          toast.error(t('an-error-has-occurred'), { id: loadingToastId });
        }
      }
    );
  };

  if (isLoading) {
    return (
      <div className="text-sm py-6 w-full flex justify-center">
        {t('loading')}...
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-sm text-red-500 py-6 text-center">
        {t('failed-to-load')}
      </div>
    );
  }

  const FORM_ID = 'create-publication-form';

  return (
    <div>
      {/* Create Publication Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        size="small"
        title={t('create-new-publication')}
        caption={t(
          'select-organization-to-publish-this-language-configuration'
        )}
        footerActions={[
          <Button
            key="cancel"
            type="button"
            onClick={() => setIsModalOpen(false)}
            label={t('cancel')}
            size="small"
            variant="smoke"
          />,
          <Button
            key="create"
            type="submit"
            form={FORM_ID}
            label={createPublication.isLoading ? t('creating') : t('create')}
            size="small"
            variant="primary"
          />
        ]}
      >
        <form
          id={FORM_ID}
          className="grid gap-4 w-full"
          onSubmit={e => {
            e.preventDefault();
            onSubmitCreate(
              (control._formValues as FormValues) || { organization_id: '' }
            );
          }}
        >
          <Controller
            control={control}
            name="organization_id"
            render={({ field: { onChange, value } }) => (
              <SelectInput
                id="organization_id"
                label={t('organization')}
                name="organization_id"
                options={customerOptions}
                value={value}
                onChange={(opt: { label: string; value: string } | null) =>
                  onChange(opt ? opt.value : '')
                }
                onInputChange={handleInputChange}
                placeholder={t('search-customers')}
                menuPlacement="bottom"
                styles={{
                  menu: base => ({ ...base, zIndex: 10000 }),
                  menuPortal: base => ({ ...base, zIndex: 10000 })
                }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                isMulti={false}
              />
            )}
          />
        </form>
      </Modal>

      <PageHeader
        title={t('publications')}
        caption={t(
          'organizations-where-this-language-configuration-is-published'
        )}
      >
        <Button
          icon={<Plus width={14} />}
          label={t('new-publication')}
          onClick={() => setIsModalOpen(true)}
        />
      </PageHeader>

      <DataGridV2<Publication>
        data={rows}
        columns={columns}
        actions={actions}
        enableRowSelection={false}
        enablePagination
        manualPagination={false} // client-side
        totalItems={rows.length}
        pageSize={25}
        pageSizeOptions={[25, 50, 75, 100]}
        enableSorting
        manualSorting={false}
        enableFiltering
        manualFiltering={false}
        showColumnFilters={false}
        filterMode="popover"
        height="70vh"
        dense
        grid={false}
        striped
        loading={isLoading || isFetching || !publications}
        getRowId={row => String(row.id)}
        onCellClick={handleCellClick}
        menuOptions={menuOptions}
        setMenuOptions={handleSetMenuOptions}
        onMenuOptionClick={handleMenuOptionClick}
      />

      {/* Delete confirm */}
      {selectedPublication && (
        <ConfirmationModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={handleConfirmDelete}
          size="small"
          title={t('delete-confirmation')}
          caption={t('are-you-sure-you-want-to-delete-this-publication')}
          confirmLabel={t('delete')}
          cancelLabel={t('cancel')}
          destructive
          autoFocusConfirm
        />
      )}
    </div>
  );
}
